import React, { memo, useMemo } from "react";
import { CnImageViewer } from "@sto/cn-ui";
import styles from './index.module.scss';
import Global from "@/utils/global";


const CImage = memo((props: { src: string, outputStatus: string }) => {
  const { src, outputStatus } = props;
  const [fileSize, setFileSize] = React.useState<string>('');

  // 获取文件大小
  React.useEffect(() => {
    const fetchFileSize = async () => {
      try {
        const response = await fetch(src, { method: 'HEAD' });
        const size = response.headers.get('Content-Length');
        if (size) {
          const sizeNum = parseInt(size);
          if (sizeNum < 1024) {
            setFileSize(`${sizeNum}B`);
          } else if (sizeNum < 1024 * 1024) {
            setFileSize(`${(sizeNum / 1024).toFixed(0)}KB`);
          } else {
            setFileSize(`${(sizeNum / 1024 / 1024).toFixed(1)}MB`);
          }
        }
      } catch (error) {
        console.error('Failed to fetch file size:', error);
      }
    };

    if (src && outputStatus?.toUpperCase() === 'FINISHED') {
      fetchFileSize();
    }
  }, [src, outputStatus]);

  // 判断是否为图片文件
  const isImageFile = (url: string) => {
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const ext = url.split('.').pop()?.toLowerCase()?.split('?')[0] || '';
    return imageExts.includes(ext);
  };

  // 根据文件URL获取对应的图标
  const getFileIcon = (url: string) => {
    const ext = url.split('.').pop()?.toLowerCase()?.split('?')[0] || '';
    const iconMap: Record<string, string> = {
      'ppt': 'https://imgcdn.sto.cn/82963C8E-2AC4-40-200x200-ppt.png',
      'pptx': 'https://imgcdn.sto.cn/82963C8E-2AC4-40-200x200-ppt.png',
      'doc': 'https://imgcdn.sto.cn/775EC0C7-4396-4E-200x200-WORD.png',
      'docx': 'https://imgcdn.sto.cn/775EC0C7-4396-4E-200x200-WORD.png',
      'xls': 'https://imgcdn.sto.cn/BD3490FA-C079-4B-200x160-excel-.png',
      'xlsx': 'https://imgcdn.sto.cn/BD3490FA-C079-4B-200x160-excel-.png',
      'csv': 'https://imgcdn.sto.cn/BD3490FA-C079-4B-200x160-excel-.png',
    };
    return iconMap[ext] || 'https://imgcdn.sto.cn/97F4A4A4-BDB9-4A-200x200-wenjian.png';
  };

  // 从 URL 中提取文件名
  const getFileName = (url: string) => {
    try {
      const urlWithoutParams = url.split('?')[0];
      const parts = urlWithoutParams.split('/');
      const fileName = parts[parts.length - 1];
      // 解码文件名
      return decodeURIComponent(fileName);
    } catch {
      return '未知文件';
    }
  };

  // 获取文件类型标签
  const getFileTypeLabel = (url: string) => {
    const ext = url.split('.').pop()?.toLowerCase()?.split('?')[0] || '';
    const typeMap: Record<string, string> = {
      'jpg': 'Image', 'jpeg': 'Image', 'png': 'Image', 'gif': 'Image', 'bmp': 'Image', 'webp': 'Image',
      'ppt': 'PPT', 'pptx': 'PPT',
      'doc': 'Word', 'docx': 'Word',
      'xls': 'Excel', 'xlsx': 'Excel', 'csv': 'CSV',
      'pdf': 'PDF',
      'txt': 'Text',
    };
    return typeMap[ext] || 'File';
  };

  const isImage = isImageFile(src);
  const fileName = getFileName(src);
  const fileType = getFileTypeLabel(src);

  const renderImage = useMemo(() => {
    if (outputStatus && outputStatus.toUpperCase() !== 'FINISHED') {
      return (
        <div style={{ margin: '10px 0' }}>
          <div style={{ 
            width: isImage ? 150 : 200, 
            height: isImage ? 100 : 60, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px dashed #d9d9d9',
            borderRadius: '4px',
            color: '#999',
            fontSize: '14px'
          }}>
            {isImage ? '图片' : '文件'}加载中...
          </div>
        </div>
      );
    }

    // 非图片文件，显示文件卡片样式
    if (!isImage) {
      return (
        <div 
          onClick={() => window.open(src, '_blank')}
          style={{ 
            margin: '10px 0',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            maxWidth: '300px',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8e8e8'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
        >
          <img 
            src={getFileIcon(src)}
            alt="file"
            style={{ 
              width: 48, 
              height: 48, 
              objectFit: 'cover',
              borderRadius: 4,
              flexShrink: 0,
            }}
          />
          <div style={{ 
            flex: 1, 
            overflow: 'hidden',
            minWidth: 0,
          }}>
            <div style={{ 
              fontSize: '14px',
              color: '#333',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 500,
            }}>
              {fileName}
            </div>
            <div style={{ 
              fontSize: '12px',
              color: '#999',
              marginTop: '4px',
            }}>
              {fileType}{fileSize ? ` · ${fileSize}` : ''}
            </div>
          </div>
        </div>
      );
    }

    // 图片文件，显示图片卡片样式
    return (
      <div 
        style={{ 
          margin: '10px 0',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          padding: '8px 12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          maxWidth: '300px',
        }}
      >
        <CnImageViewer 
          src={src} 
          width={48} 
          height={48}
          style={{ 
            flexShrink: 0,
            borderRadius: 4,
          }} 
        />
        <div style={{ 
          flex: 1, 
          overflow: 'hidden',
          minWidth: 0,
        }}>
          <div style={{ 
            fontSize: '14px',
            color: '#333',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 500,
          }}>
            {fileName}
          </div>
          <div style={{ 
            fontSize: '12px',
            color: '#999',
            marginTop: '4px',
          }}>
            {fileType}
          </div>
        </div>
      </div>
    );
  }, [src, outputStatus, isImage, fileName, fileType]);

  return (
    renderImage
  );
});

const ReactMarkdownComponents = ({ from, requestId, outputStatus }) => {
  return {
    img: ({ src }) => <CImage key={src} src={src} outputStatus={outputStatus} />,
    span: (props) => (
      <span
        {...props}
        className={`${props.className || ''} ${styles.p} ${
          from === 'question' ? styles.fromQuestion : ''
        }`}
      />
    ),
    p: (props) => (
      <p
        {...props}
        className={`${props.className} ${
          from === 'question' ? styles.fromQuestion : ''
        } ${styles.p}`}
      />
    ),
    a: ({ href, ...props }) => (
      <span
        {...props}
        className={`${props.className} ${styles.p} ${
          from === 'question' ? styles.fromQuestion : ''
        }`}
        onClick={() => Global.jumpTo(href, props.children?.[0], requestId)}
        style={{ color: '#FF6F01', cursor: 'pointer' }}
      />
    ),
    table: { 
      component: ({ children, ...props }: any) => <div 
        className={`${styles.tableContainer} ${props.className}`}
        style={{ 
          overflow: 'auto', 
          margin: '10px 0',
          border: '1px solid #ddd',
          borderRadius: '6px',
          maxHeight: '300px',
        }}
      >
        <table className={styles.table} {...props}>{children}</table> 
      </div>
    },
    th: { 
      component: ({ children, ...props }: any) => (
        <th 
          className={styles.th}
          style={{
            padding: '8px 6px',
            textAlign: 'center',
            fontWeight: '500',
            backgroundColor: '#fafafa',
            borderBottom: '2px solid #e8e8e8',
            color: '#1D2129',
            whiteSpace: 'nowrap',
            fontSize: '12px'
          }}
          {...props}
        >{children}</th> 
      )
    },
    thead: {
      component: ({ children, ...props }: any) => (
        <thead 
          {...props} 
          className={styles.thead}
          style={{ backgroundColor: '#fafafa' }}
        >{children}</thead>
      )
    },
    tbody: {
      component: ({ children, ...props }: any) => (
        <tbody 
          {...props} 
          className={styles.tbody}
          style={{ backgroundColor: '#ffffff' }}
        >{children}</tbody>
      )
    },
    tr: {
      component: ({ children, ...props }: any) => (
        <tr
          {...props}
          className={styles.tr}
          style={{
            borderBottom: '1px solid #e8e8e8'
          }}
        >{children}</tr>
      )
    },
    td: {
      component: ({ children, ...props }: any) => (
        <td
          {...props}
          className={styles.td}
          style={{
            padding: '8px 6px',
            textAlign: 'center',
            borderBottom: '1px solid #f0f0f0',
            color: '#1D2129',
            fontSize: '12px',
            whiteSpace: 'nowrap'
          }}
        >{children}</td>
      )
    },
  };
};

export default ReactMarkdownComponents;
